import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFilePdf, faLock } from '@fortawesome/free-solid-svg-icons';
import { toast } from '@/lib/swal';
import { CustomerSectionCard } from '@/components/customer-portal/CustomerSectionCard';
import { CustomerDocumentCard } from '@/components/customer-portal/CustomerDocumentCard';
import { CustomerKeyValue } from '@/components/customer-portal/CustomerKeyValue';
import { customerReportService } from '@/services/customer-report.service';

interface Props {
  jobId: string;
}

const UNAVAILABLE = 'Inspection report is not available.';

function fmtDate(v: string | null): string {
  if (!v) return '—';
  return new Date(v).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

/**
 * Read-only Inspection Report view for the Customer Portal. Shows metadata and a
 * streamed PDF download for the job's approved & published report. The backend
 * is the sole authority — a 404 (unpublished / unapproved / cross-customer /
 * missing, indistinguishable) renders the same "not available" message. Report
 * state is never inferred client-side; no PDF is generated or cached here.
 */
export function CustomerReportSection({ jobId }: Props) {
  const [downloading, setDownloading] = useState(false);

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['customer-portal', 'report', jobId],
    retry: false,
    queryFn: () => customerReportService.getReport(jobId),
  });

  const handleDownload = async () => {
    setDownloading(true);
    try {
      await customerReportService.download(jobId);
    } catch {
      toast.error('The report could not be downloaded. Please try again.');
    } finally {
      setDownloading(false);
    }
  };

  if (isLoading) {
    return (
      <CustomerSectionCard title="Inspection Report" icon={faFilePdf}>
        <div className="space-y-3" data-testid="customer-report-loading">
          <div className="customer-shimmer h-20 rounded-customer" />
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="customer-shimmer h-9 rounded-customer-sm" />
            ))}
          </div>
        </div>
      </CustomerSectionCard>
    );
  }

  if (isError || !data) {
    const is404 = axios.isAxiosError(error) && error.response?.status === 404;
    return (
      <CustomerSectionCard title="Inspection Report" icon={faFilePdf}>
        <p
          className="flex items-center gap-2 text-sm text-customer-text-secondary"
          data-testid="customer-report-unavailable"
        >
          <FontAwesomeIcon icon={faLock} className="h-3.5 w-3.5" aria-hidden="true" />
          {is404 || !error
            ? UNAVAILABLE
            : 'Unable to load the report right now. Please try again later.'}
        </p>
      </CustomerSectionCard>
    );
  }

  const eq = data.equipment;
  const equipmentLabel = [eq.name, eq.modelNumber].filter(Boolean).join(' · ') || eq.assetNumber;

  return (
    <CustomerSectionCard
      title="Inspection Report"
      icon={faFilePdf}
      description="The approved service report for this job."
    >
      <div className="space-y-4">
        <CustomerDocumentCard
          kind="Service report"
          reference={data.reportNumber}
          meta={['PDF document', `Approved ${fmtDate(data.approvalDate)}`]}
          available
          onDownload={() => void handleDownload()}
          downloading={downloading}
          downloadLabel="Download PDF"
          downloadTestId="customer-report-download"
        />
        <CustomerKeyValue
          layout="grid"
          data-testid="customer-report-view"
          items={[
            { label: 'Equipment', value: equipmentLabel },
            { label: 'Asset Number', value: eq.assetNumber },
            { label: 'Serial Number', value: eq.serialNumber },
            { label: 'Inspection Date', value: fmtDate(data.inspectionDate) },
            { label: 'Generated Date', value: fmtDate(data.generatedDate) },
            { label: 'Approval Date', value: fmtDate(data.approvalDate) },
            /*
              B6.11 (G-1). This showed `Default Inspection Report v1.0` — the
              renderer's build-time constants, stored on the report row at
              generation, which name no template that ever drew anything. The
              authoritative provenance is the version of the layout the report
              actually froze, and it is what the PDF footer prints, so the screen
              and the document agree by construction. The template NAME is
              deliberately not shown: it is tenant-authored free text on a
              customer-facing surface, and the version alone identifies the
              design for audit — the same reasoning drawFooter applies.
            */
            {
              label: 'Template',
              value: data.layoutVersion !== null ? `Report v${data.layoutVersion}` : '—',
            },
          ]}
        />
      </div>
    </CustomerSectionCard>
  );
}
